import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Pause, X, Youtube, Plane, Car, Leaf, Sparkles, Film, Share2, Headphones, QrCode } from "lucide-react";
import { Header } from "@/components/Header";
import { extractYouTubeId } from "@/lib/youtube";
import { useLang } from "@/context/LangContext";
import { VisionCredits } from "@/components/VisionCredits";
import QRCode from "qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1544015759-237f87d55ef3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const COAST_IMG = "https://images.unsplash.com/photo-1531201890865-fb64780d16e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SHARE_COVER = "https://static.prod-images.emergentagent.com/jobs/0e3d4d61-ed21-4f86-b551-f282d91748f8/images/4d355c0c50a71bc288960f7023d4a92f6811f653895e56204dbc476422a66b61.jpeg";

// Curated, shorter scenic clips that play back-to-back as one continuous film.
const FILM_SEQUENCE = [
  "q0rMXhbEeX0", // Sri Lanka From Above
  "evZDvIGpFtA", // Diyaluma Falls
  "KEOLo6_8lSk", // Trincomalee
  "RvJ4DlFfS98", // Nilaveli
  "M1laS8cSVdo", // Arugam Bay drone
  "X05zSEYickg", // Our Lady of Madhu
];

const CLIMATE = [
  { icon: Car, big: "1 hour", small: "From warm golden coast to cool misty hills — on the expressway" },
  { icon: Plane, big: "30–45 min", small: "Beach sunrise to mountain mist — by air across the island" },
  { icon: Sparkles, big: "7 climates", small: "Rainforest, dry plains, highlands & reef — on one small isle" },
  { icon: Leaf, big: "Unspoiled", small: "Greenery kept green by nature herself, lovingly preserved" },
];

const VideoModal = ({ src, onClose, title }) => (
  <div onClick={onClose} data-testid="serenbity-video-modal" className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(8,6,3,0.94)" }}>
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl">
      <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: "56.25%", background: "#000" }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={src}
          title={title || "Serenbity film"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
    <button onClick={onClose} aria-label="Close" data-testid="serenbity-video-close" className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-full" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}><X size={22} /></button>
  </div>
);

export default function Serenbity() {
  const { lang } = useLang();
  const [chapters, setChapters] = useState([]);
  const [modal, setModal] = useState(null); // {src, title}
  const [narration, setNarration] = useState({});
  const [narrPlaying, setNarrPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/heritage`).then((r) => {
      const list = [];
      (r.data || []).forEach((s) => {
        (s.videos || []).forEach((v) => {
          const id = extractYouTubeId(v.url);
          if (id) list.push({ id, label: v.label || "Watch", story: s.title, poster: s.image_url });
        });
      });
      setChapters(list);
    }).catch(() => {});
    axios.get(`${API}/serenbity/narration`).then((r) => setNarration(r.data?.langs || {})).catch(() => {});
  }, []);

  const narrUrl = narration[lang] || narration.en;
  const toggleNarration = () => {
    const a = audioRef.current;
    if (!a) return;
    if (narrPlaying) a.pause();
    else a.play();
  };

  const playFilm = () => {
    if (audioRef.current) audioRef.current.pause();
    const [first, ...rest] = FILM_SEQUENCE;
    const src = `https://www.youtube.com/embed/${first}?rel=0&modestbranding=1&playsinline=1${rest.length ? `&playlist=${rest.join(",")}` : ""}`;
    setModal({ src, title: "Serenbity — the film" });
  };

  const playChapter = (id) => {
    if (audioRef.current) audioRef.current.pause();
    setModal({ src: `https://www.youtube.com/embed/${id}?rel=0&playsinline=1`, title: "Chapter" });
  };

  const shareFilm = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/serenbity`;
    const data = { title: "Serenbity · Sri Lanka", text: "Serenbity — where Serendib meets Serenity. A little film about the Resplendent Isle. 🇱🇰", url };
    try {
      if (navigator.share) { await navigator.share(data); }
      else { await navigator.clipboard.writeText(url); alert("Link copied — share Sri Lanka with someone you love 🌴"); }
    } catch (e) { /* user cancelled */ }
  };

  const printPoster = async () => {
    const link = `${process.env.REACT_APP_BACKEND_URL}/api/share/serenbity`;
    let qr = "";
    try { qr = await QRCode.toDataURL(link, { width: 640, margin: 1, color: { dark: "#2a1608", light: "#ffffff" } }); } catch (e) {}
    const w = window.open("", "_blank", "width=820,height=1160");
    if (!w) { alert("Please allow pop-ups to open the printable poster."); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Serenbity — Sri Lanka</title>
    <style>
      @page { size: A4 portrait; margin: 0; }
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: Georgia, 'Times New Roman', serif; background:#140e08; color:#2a1608; }
      .poster { width:100%; min-height:100vh; background:#fdf6e9; display:flex; flex-direction:column; }
      .cover { position:relative; height:46vh; overflow:hidden; }
      .cover img { width:100%; height:100%; object-fit:cover; display:block; }
      .cover .shade { position:absolute; inset:0; background:linear-gradient(180deg,rgba(20,14,8,.15),rgba(253,246,233,1)); }
      .eyebrow { letter-spacing:.35em; font-size:12px; text-transform:uppercase; color:#8c6a2e; font-family:Arial,sans-serif; }
      .body { flex:1; text-align:center; padding:28px 40px 44px; display:flex; flex-direction:column; align-items:center; }
      h1 { font-size:68px; line-height:1; color:#b8791a; margin:6px 0 4px; letter-spacing:1px; }
      .tag { font-style:italic; font-size:24px; color:#5a4327; }
      .sub { font-family:Arial,sans-serif; font-size:13px; color:#8c6a2e; margin-top:6px; letter-spacing:.08em; }
      .quote { max-width:560px; font-size:19px; line-height:1.55; color:#3a2a18; margin:22px auto 10px; }
      .qrwrap { margin-top:auto; display:flex; flex-direction:column; align-items:center; gap:10px; }
      .qr { width:200px; height:200px; border:10px solid #fff; border-radius:16px; box-shadow:0 6px 20px rgba(0,0,0,.18); }
      .scan { font-family:Arial,sans-serif; font-size:15px; font-weight:bold; color:#2a1608; }
      .url { font-family:Arial,sans-serif; font-size:11px; color:#8c6a2e; word-break:break-all; }
      .credit { font-family:Arial,sans-serif; font-size:11px; color:#a98b55; margin-top:14px; letter-spacing:.1em; }
      @media print { body { background:#fff; } .poster{ min-height:auto; } }
    </style></head>
    <body><div class="poster">
      <div class="cover"><img id="cv" src="${SHARE_COVER}"/><div class="shade"></div></div>
      <div class="body">
        <div class="eyebrow">A love letter to an island</div>
        <h1>Serenbity</h1>
        <div class="tag">where Serendib meets Serenity</div>
        <div class="sub">THE RESPLENDENT ISLE · SERENDIB · TAPROBANE · SRI LANKA</div>
        <p class="quote">A single teardrop of land in the warm Indian Ocean — golden beaches, misty mountains, ancient cities and the kindest of smiles, all within a day's gentle reach. You don't visit Sri Lanka. She stays with you.</p>
        <div class="qrwrap">
          ${qr ? `<img class="qr" src="${qr}"/>` : ""}
          <div class="scan">Scan to watch the film 🎬</div>
          <div class="url">${link}</div>
          <div class="credit">NEOX · SERENBITY</div>
        </div>
      </div>
    </div>
    <script>
      var c=document.getElementById('cv');
      function go(){ setTimeout(function(){ window.focus(); window.print(); }, 400); }
      if(c.complete){ go(); } else { c.onload=go; c.onerror=go; }
    </script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="serenbity-hero">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(12,9,5,0.55) 0%, rgba(12,9,5,0.35) 35%, rgba(12,9,5,0.92) 100%)" }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 pt-6 pb-14">
          <Link to="/rooms/sindu-kamare" data-testid="serenbity-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold text-white/85 hover:text-white">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="mt-16 sm:mt-24">
            <span className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-[0.25em] text-white/80">
              <Film size={14} /> A love letter to an island
            </span>
            <h1 className="font-en font-extrabold leading-[0.95] mt-3 text-5xl sm:text-6xl lg:text-7xl"
              style={{ background: "linear-gradient(135deg,#ffe8b0 0%,#f4b41a 45%,#e07b39 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              Serenbity
            </h1>
            <p className="font-en text-xl sm:text-2xl italic text-white/90 mt-3">where Serendib meets Serenity</p>
            <p className="font-ui text-sm sm:text-base text-white/75 mt-1 tracking-wide">The Resplendent Isle · Taprobane · Serendib · <span className="font-bold text-white/90">Sri Lanka</span></p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={playFilm} data-testid="serenbity-play-film"
                className="inline-flex items-center gap-2.5 h-14 px-7 rounded-full font-ui font-extrabold text-base transition-transform active:scale-95 hover:scale-[1.03]"
                style={{ background: "linear-gradient(135deg,#f4b41a 0%,#d97706 55%,#8c1c13 100%)", color: "#fff6e5", boxShadow: "0 10px 30px rgba(140,28,19,0.45)" }}>
                <Play size={20} fill="currentColor" /> Play the Film
              </button>
              {narrUrl && (
                <button onClick={toggleNarration} data-testid="serenbity-narration-btn"
                  className="inline-flex items-center gap-2.5 h-14 px-6 rounded-full font-ui font-bold text-base transition-transform active:scale-95 hover:scale-[1.03]"
                  style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "2px solid rgba(255,255,255,0.5)", backdropFilter: "blur(6px)" }}>
                  {narrPlaying ? <Pause size={19} fill="currentColor" /> : <Headphones size={19} />} {narrPlaying ? "Pause story" : "Listen to the story"}
                </button>
              )}
            </div>
            <p className="font-ui text-xs text-white/60 mt-3">▸ Six scenes, one gentle journey — plays back to back</p>
          </div>
        </div>
      </section>

      {/* POETIC INTRO */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 py-12 sm:py-16" data-testid="serenbity-intro">
        <p className="font-en text-2xl sm:text-3xl font-bold leading-snug" style={{ color: "var(--text)" }}>
          One small teardrop of land in the warm Indian Ocean —
          <span style={{ color: "var(--accent)" }}> small enough to take in with a single sweep of the eye from the sky, yet deep enough to hold a thousand lifetimes within.</span>
        </p>
        <div className="mt-6 space-y-4 font-ui text-base leading-relaxed" style={{ color: "var(--text-2)" }}>
          <p>
            Long ago, sailors who stumbled upon her shores called her <span className="italic">Serendib</span> — the island of happy discovery. From that one name the world borrowed a word it still treasures today: <span className="italic">serendipity</span>, the gift of finding something wonderful you were never even looking for. That, in a single word, is Sri Lanka. You arrive chasing one postcard, and she quietly presses ten more into your hands before you leave.
          </p>
          <p>
            She carries her beauty the way a graceful woman wears a single jasmine in her hair — unhurried, unstudied, never for show. Emerald tea terraces comb the shoulders of her mountains. Waterfalls unspool like silver ribbons through the mist. Rivers wander where they please; elephants cross the old plains at dusk, and leopards move through the grass like travelling shadow. Her green is not trimmed and tamed but <span className="font-semibold" style={{ color: "var(--text)" }}>wild, breathing and unspoiled</span> — a rare corner of the earth where nature is still kept by nature herself.
          </p>
          <p>
            Close your eyes and she comes to you in scents and sounds: the warm hush of cinnamon and clove, soft rain on a temple roof, the far-off boom of the sea, a bell, a chanted prayer, a teaspoon against a cup. More than two thousand years of kings, poets and pilgrims have walked this soil — and somehow she has stayed gentle through it all.
          </p>
          <p>
            And everywhere, her people: warm as the midday sun, quick to smile, slow to forget a guest. Four great faiths, three languages, a hundred festivals — and one wide, open heart.
          </p>
          <p>
            Some of her wonders she keeps for herself alone. Here — and almost nowhere else on earth — you may greet the <span className="font-semibold" style={{ color: "var(--text)" }}>greatest creature of the ocean, the blue whale, at dawn, and the greatest animal of the land, the elephant, by afternoon</span>. Here grows the world's finest cinnamon, so coveted that empires once crossed oceans to find it. Leopards pad through Yala as nowhere else on the planet; sapphires sleep blue in the earth of Ratnapura; peacocks open the morning with their cry, fireflies lift from the paddy at dusk, and by the roadside a smiling hand splits open a golden king-coconut — cool, sweet and free — just for you.
          </p>
          <p>
            And oh, her perfume. The white temple-flower <span className="italic">araliya</span> on the warm night air. Cinnamon drying in the sun. Jasmine and the first rain falling on thirsty earth. Everywhere a soundtrack she composes herself — the long hush and roll of the waves, a temple bell drifting over the water, the call of the <span className="italic">koha</span>, and somewhere, always, the soft laughter of children.
          </p>
        </div>
      </section>

      {/* ANTHEM TRIBUTE */}
      <section className="max-w-3xl mx-auto px-6 pb-6 text-center" data-testid="serenbity-anthem">
        <div className="mx-auto w-14 rounded-full" style={{ height: "3px", background: "var(--accent)" }} />
        <p className="font-en text-2xl sm:text-3xl italic font-bold leading-snug mt-7" style={{ color: "var(--accent)" }}>
          “Blue water, shining eyes —<br className="hidden sm:block" /> Sri Lanka, paradise.”
        </p>
        <p className="font-ui text-sm mt-3" style={{ color: "var(--text-2)" }}>
          — the old anthem that once carried her name across the skies
        </p>
        <button onClick={() => playChapter("bL5hAUW1STU")} data-testid="serenbity-anthem-play" className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full font-ui font-bold text-sm transition-transform active:scale-95 hover:scale-[1.03]" style={{ background: "var(--card)", color: "var(--text)", border: "2px solid var(--accent)" }}>
          <Play size={15} fill="var(--accent)" style={{ color: "var(--accent)" }} /> Play the anthem
        </button>
      </section>

      {/* CLIMATE MARVEL */}
      <section className="max-w-4xl mx-auto px-5 sm:px-6 pb-14" data-testid="serenbity-climate">
        <div className="rounded-3xl overflow-hidden" style={{ border: "2px solid var(--border)", background: "var(--card)" }}>
          <div className="relative h-44 sm:h-56">
            <img src={COAST_IMG} alt="Sri Lanka coast" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(20,14,8,0.85) 100%)" }} />
            <div className="absolute bottom-4 left-5 right-5">
              <h2 className="font-en text-2xl sm:text-3xl font-extrabold text-white leading-tight">Every season in a single day</h2>
              <p className="font-ui text-sm text-white/85 mt-1">Nowhere else do so many worlds sit so close together.</p>
            </div>
          </div>
          <div className="p-5 sm:p-7">
            <p className="font-ui text-base leading-relaxed" style={{ color: "var(--text-2)" }}>
              Here is her quiet, almost unbelievable magic. Dip your feet in a warm golden sea at sunrise, and within <span className="font-bold" style={{ color: "var(--accent)" }}>about an hour on the expressway</span> — or barely <span className="font-bold" style={{ color: "var(--accent)" }}>thirty to forty-five minutes by air</span> — you can be standing high among cool, cloud-wrapped mountains, reaching for a shawl. Rainforest and dry savannah, tea-country mist and coral reef, all gathered on one small island, all within a single gentle day's reach. Few places on earth hold so many worlds so lovingly close together.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              {CLIMATE.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} data-testid={`serenbity-climate-${i}`} className="rounded-2xl p-4 text-center" style={{ background: "var(--brass-light)", border: "1px solid var(--border)" }}>
                    <span className="grid place-items-center w-10 h-10 rounded-full mx-auto mb-2" style={{ background: "var(--card)", color: "var(--accent)" }}><Icon size={18} /></span>
                    <div className="font-en text-xl font-extrabold" style={{ color: "var(--text)" }}>{c.big}</div>
                    <div className="font-ui text-xs leading-snug mt-1" style={{ color: "var(--text-2)" }}>{c.small}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="max-w-4xl mx-auto px-5 sm:px-6" data-testid="serenbity-chapters">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <h2 className="font-en text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text)" }}>The island in chapters</h2>
            <p className="font-ui text-sm mt-1" style={{ color: "var(--text-2)" }}>Tap any scene to watch it on its own.</p>
          </div>
          <button onClick={playFilm} data-testid="serenbity-play-film-2" className="shrink-0 inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "2px solid var(--border)" }}>
            <Play size={16} style={{ color: "var(--accent)" }} fill="var(--accent)" /> Play all
          </button>
        </div>
        {chapters.length === 0 ? (
          <p className="text-center font-ui text-sm py-10" style={{ color: "var(--text-muted)" }} data-testid="serenbity-empty">The film is being assembled. 🎬</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="serenbity-chapter-grid">
            {chapters.map((c, i) => (
              <button key={`${c.id}-${i}`} onClick={() => playChapter(c.id)} data-testid={`serenbity-chapter-${i}`}
                className="group relative text-left rounded-2xl overflow-hidden transition-transform active:scale-[0.98] hover:scale-[1.01]" style={{ border: "2px solid var(--border)", background: "#000" }}>
                <div className="relative" style={{ paddingBottom: "56.25%" }}>
                  {c.poster && <img src={c.poster} alt={c.story} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity" />}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(8,6,3,0.9) 100%)" }} />
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="grid place-items-center w-14 h-14 rounded-full transition-transform group-hover:scale-110" style={{ background: "rgba(244,180,26,0.92)", color: "#2a1608", boxShadow: "0 6px 20px rgba(0,0,0,0.4)" }}><Play size={24} fill="currentColor" /></span>
                  </span>
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="font-en text-lg font-extrabold text-white leading-tight">{c.story}</div>
                    <div className="inline-flex items-center gap-1.5 font-ui text-xs font-bold mt-1" style={{ color: "#f4b41a" }}><Youtube size={13} /> {c.label}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="text-center mt-14 mb-4">
          <p className="font-en text-2xl sm:text-3xl font-bold" style={{ color: "var(--text)" }}>You don't visit Sri Lanka.</p>
          <p className="font-en text-2xl sm:text-3xl font-bold" style={{ color: "var(--accent)" }}>She stays with you.</p>
          <p className="font-ui text-sm mt-3" style={{ color: "var(--text-2)" }}>Serenbity · the Resplendent Isle</p>
        </div>

        {/* TOURISM CTA */}
        <div className="rounded-3xl p-6 sm:p-8 text-center mt-8" data-testid="serenbity-cta"
          style={{ background: "linear-gradient(135deg,#2a1608 0%,#4a2410 100%)", border: "2px solid var(--border)" }}>
          <h3 className="font-en text-2xl sm:text-3xl font-extrabold" style={{ color: "#ffe8b0" }}>Come and see for yourself</h3>
          <p className="font-ui text-sm sm:text-base mt-3 max-w-xl mx-auto leading-relaxed" style={{ color: "rgba(255,240,215,0.85)" }}>
            No film, however lovely, can hold the whole of her — some things must be breathed, tasted and walked. Whether you come to wander wide-eyed, to rest a tired heart, or simply to find your way home again, Sri Lanka keeps her door open, her lamp lit and her kettle warm. There is always room for one more at her table.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button onClick={shareFilm} data-testid="serenbity-share"
              className="inline-flex items-center gap-2.5 h-13 px-7 py-3 rounded-full font-ui font-extrabold text-base transition-transform active:scale-95 hover:scale-[1.03]"
              style={{ background: "linear-gradient(135deg,#f4b41a 0%,#d97706 55%,#8c1c13 100%)", color: "#fff6e5", boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}>
              <Share2 size={18} /> Share Sri Lanka with the world
            </button>
            <button onClick={printPoster} data-testid="serenbity-poster"
              className="inline-flex items-center gap-2.5 h-13 px-6 py-3 rounded-full font-ui font-bold text-base transition-transform active:scale-95 hover:scale-[1.03]"
              style={{ background: "transparent", color: "#ffe8b0", border: "2px solid rgba(255,232,176,0.55)" }}>
              <QrCode size={18} /> Printable QR poster
            </button>
          </div>
          <p className="font-ui text-[11px] mt-5 leading-relaxed" style={{ color: "rgba(255,240,215,0.5)" }} data-testid="serenbity-credits">
            Music: “Meditation Impromptu” — Kevin MacLeod (CC BY 4.0). Nature sounds: Wikimedia Commons — ocean &amp; elephant (CC0), waterfall (CC BY 4.0), birdsong (CC BY-SA 4.0).
          </p>
        </div>
      </section>

      <VisionCredits onShare={shareFilm} />

      {modal && <VideoModal src={modal.src} title={modal.title} onClose={() => setModal(null)} />}
      <audio ref={audioRef} src={narrUrl || undefined} preload="none" onPlay={() => setNarrPlaying(true)} onPause={() => setNarrPlaying(false)} onEnded={() => setNarrPlaying(false)} />
    </div>
  );
}
